/**
 * Intent inference — pure, unit-testable rules.
 *
 * Privacy contract: everything here operates on the anonymous, session-local
 * VisitorState plus a handful of ephemeral signals tracked by BehaviorEngine.
 * Nothing is transmitted anywhere; nothing persists beyond the tab session.
 * The output is an interest classification ("what is this visitor looking
 * for"), never an identity.
 */

import type { IntentId, LabHint, VisitorState, ZoneId } from '../lab/types';

/** Ephemeral behavioral signals tracked locally by BehaviorEngine. */
export interface BehaviorSignals {
  /** Approximate time spent per zone, in ms (accrued while the tab is open). */
  zoneDwellMs: Partial<Record<ZoneId, number>>;
  /** How many knowledge-graph nodes received focus (NODE_FOCUSED events). */
  nodeFocusCount: number;
  /** AGENT_SELECTED events seen on the bus (fallback for agentInteractions). */
  agentSelections: number;
  /** Milliseconds since the behavioral engine mounted. */
  sessionMs: number;
}

export interface IntentResult {
  intent: IntentId;
  confidence: Partial<Record<IntentId, number>>;
}

/* ── Tunable thresholds (exported so tests can assert against them) ───── */

/** A top score below this confidence yields 'unknown'. */
export const INTENT_MIN_CONFIDENCE = 0.45;
/** Dwell on the research zone that counts as "long" for researcher intent. */
export const RESEARCH_DWELL_MS = 45_000;
/** A session younger than this still counts as "quick" for explorer intent. */
export const EXPLORER_FAST_MS = 120_000;
/**
 * Damping constant added to the score total before normalization, so a single
 * weak signal cannot reach full confidence on its own.
 */
export const INTENT_DAMPING = 0.8;

/** Knowledge-zone dwell that triggers the knowledge-path hint. */
export const KNOWLEDGE_DWELL_HINT_MS = 25_000;
/** No hint fires before the session is this old. */
export const HINT_SESSION_MIN_MS = 40_000;
/** Minimum spacing between two hints. */
export const HINT_GAP_MS = 60_000;

const round2 = (x: number) => Math.round(x * 100) / 100;

/**
 * Score the visitor against each intent, normalize to 0..1 and pick the top.
 *
 * Rules (additive scores, damped normalization: conf = score / (total + 0.8)):
 *   researcher — ≥2 research entries viewed (+2), exactly one (+0.5),
 *                ≥45s dwell in the research zone (+1.5),
 *                publications/archive visited (+1).
 *   developer  — ≥2 agent interactions (+1.5), exactly one (+0.5),
 *                both 'agents' and 'knowledge' zones visited (+1.5),
 *                the open-source runtime project opened (+0.5),
 *                ≥3 knowledge nodes focused (+0.75).
 *   partner    — ≥3 projects viewed (+0.5), gateway (about/contact) visited
 *                (+0.5), both together (+2 synergy).
 *   explorer   — ≥4 zones with shallow depth ≤1 (+2),
 *                ≥3 zones quickly (<120s) with shallow depth (+0.5).
 * If the top confidence is below 0.45 the intent is 'unknown'.
 */
export function inferIntent(
  visitor: VisitorState,
  signals: BehaviorSignals,
): IntentResult {
  const depth = visitor.viewedProjects.length + visitor.viewedResearch.length;
  const zones = visitor.visitedZones;
  const has = (z: ZoneId) => zones.includes(z);
  const agentCount = Math.max(visitor.agentInteractions, signals.agentSelections);

  const score: Record<Exclude<IntentId, 'unknown'>, number> = {
    researcher: 0,
    developer: 0,
    partner: 0,
    explorer: 0,
  };

  // researcher
  if (visitor.viewedResearch.length >= 2) score.researcher += 2;
  else if (visitor.viewedResearch.length === 1) score.researcher += 0.5;
  if ((signals.zoneDwellMs.research ?? 0) >= RESEARCH_DWELL_MS) score.researcher += 1.5;
  if (has('archive')) score.researcher += 1;

  // developer
  if (agentCount >= 2) score.developer += 1.5;
  else if (agentCount === 1) score.developer += 0.5;
  if (has('agents') && has('knowledge')) score.developer += 1.5;
  if (visitor.viewedProjects.includes('agent-native-universe')) score.developer += 0.5;
  if (signals.nodeFocusCount >= 3) score.developer += 0.75;

  // partner
  const manyProjects = visitor.viewedProjects.length >= 3;
  const gatewayVisited = has('gateway');
  if (manyProjects) score.partner += 0.5;
  if (gatewayVisited) score.partner += 0.5;
  if (manyProjects && gatewayVisited) score.partner += 2;

  // explorer — many zones, shallow depth
  if (zones.length >= 4 && depth <= 1) score.explorer += 2;
  if (zones.length >= 3 && depth <= 1 && signals.sessionMs < EXPLORER_FAST_MS) {
    score.explorer += 0.5;
  }

  const total = score.researcher + score.developer + score.partner + score.explorer;
  const confidence: Partial<Record<IntentId, number>> = {};
  if (total <= 0) return { intent: 'unknown', confidence };

  const denom = total + INTENT_DAMPING;
  const order: Exclude<IntentId, 'unknown'>[] = [
    'researcher',
    'developer',
    'partner',
    'explorer',
  ];
  let top: IntentId = 'unknown';
  let topConf = 0;
  for (const id of order) {
    if (score[id] <= 0) continue;
    const c = round2(score[id] / denom);
    confidence[id] = c;
    if (c > topConf) {
      topConf = c;
      top = id;
    }
  }

  if (topConf < INTENT_MIN_CONFIDENCE) return { intent: 'unknown', confidence };
  return { intent: top, confidence };
}

/* ── Proactive hints ──────────────────────────────────────────────────── */

export interface HintContext {
  visitor: VisitorState;
  signals: BehaviorSignals;
  /** Current pathname — decides the knowledge-path hint variant. */
  pathname: string;
  /** ms since the last hint was shown; null if none was shown this session. */
  msSinceLastHint: number | null;
  /** Hint ids already shown or dismissed this session (fires once each). */
  firedIds: ReadonlySet<string>;
  /** True while a hint is currently visible — at most one at a time. */
  hintOpen: boolean;
}

/**
 * Pick the next proactive hint, or null. Quiet by design: nothing before 40s
 * of session, at least 60s between hints, one visible at a time, each id at
 * most once per session.
 */
export function nextHint(ctx: HintContext): LabHint | null {
  if (ctx.hintOpen) return null;
  if (ctx.signals.sessionMs < HINT_SESSION_MIN_MS) return null;
  if (ctx.msSinceLastHint !== null && ctx.msSinceLastHint < HINT_GAP_MS) return null;

  const candidates: LabHint[] = [];

  // Rule 1 — sustained interest in the knowledge system.
  const knowledgeInterest =
    (ctx.signals.zoneDwellMs.knowledge ?? 0) > KNOWLEDGE_DWELL_HINT_MS ||
    ctx.signals.nodeFocusCount >= 2;
  if (knowledgeInterest) {
    candidates.push(
      ctx.pathname.startsWith('/knowledge')
        ? {
            id: 'knowledge-path',
            text: 'You seem interested in how knowledge moves through the laboratory. This system is built on the laboratory’s knowledge-graph research.',
            actionLabel: 'Read the research behind it →',
            href: '/research/knowledge-graphs',
          }
        : {
            id: 'knowledge-path',
            text: 'You seem interested in how knowledge moves through the laboratory.',
            actionLabel: 'Explore the Knowledge System →',
            href: '/knowledge',
          },
    );
  }

  // Rule 2 — several projects viewed.
  if (ctx.visitor.viewedProjects.length >= 3) {
    candidates.push({
      id: 'applied',
      text: 'Looking for what the laboratory can build?',
      actionLabel: 'Explore applied research →',
      href: '/research',
    });
  }

  // Rule 3 — researcher intent confirmed.
  if (ctx.visitor.intent === 'researcher') {
    candidates.push({
      id: 'publications',
      text: 'The laboratory keeps its claims checkable.',
      actionLabel: 'See publications & artifacts →',
      href: '/publications',
    });
  }

  for (const c of candidates) {
    if (!ctx.firedIds.has(c.id)) return c;
  }
  return null;
}
